// src/pages/BlogPost.tsx
import React from 'react';
import { Container } from 'react-bootstrap';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectPostById, selectPostIdByLegacySlug } from '../blog/blogSlice';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const isExternal = (href?: string) => !!href && /^https?:\/\//i.test(href);

const BlogPost: React.FC = () => {
  const { id } = useParams();
  const { i18n, t } = useTranslation();
  const lang = i18n.language || 'cs';

  const postById = useSelector(selectPostById(id || ''));

  // If not found by id, try interpreting the param as a legacy slug and redirect
  const legacyResolvedId = useSelector(selectPostIdByLegacySlug(id || ''));
  if (!postById && legacyResolvedId) {
    return <Navigate to={`/blog/${legacyResolvedId}`} replace />;
  }

  const post: any = postById;

  if (!post) {
    return (
      <Container style={{ paddingTop: 96 }}>
        <h2>{t('blog.post.notFoundTitle')}</h2>
        <p>
          <Link to="/blog">{t('blog.post.backToList')}</Link>
        </p>
      </Container>
    );
  }

  const tr = post.translations?.[lang] || post.translations?.cs || Object.values(post.translations || {})[0];

  return (
    <Container style={{ paddingTop: 96, paddingBottom: 24 }}>
      <p className="text-muted mb-1">{new Date(post.date).toLocaleDateString()}</p>
      <h1 className="mb-3">{tr?.title}</h1>
      <div className="blog-content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ node, href, children, ...props }) => (
              <a
                href={href}
                target={isExternal(href) ? '_blank' : undefined}
                rel={isExternal(href) ? 'nofollow noopener noreferrer' : undefined}
                {...props}
              >
                {children}
              </a>
            ),
            img: ({ node, alt, ...props }) => (
              // @ts-ignore ReactMarkdown types are permissive here
              <img loading="lazy" alt={typeof alt === 'string' ? alt : ''} {...props} />
            ),
            // Ensure only one H1 on the page: downgrade markdown h1 to h2
            h1: ({ node, children, ...props }) => <h2 {...props}>{children}</h2>,
          }}
        >
          {tr?.content || ''}
        </ReactMarkdown>

        {post.id === '2025-12-02-budget-simplified' && (
          <div className="d-flex flex-column flex-md-row gap-2 justify-content-md-center mt-3">
            <img
              src="/blog/budget.jpg"
              alt="Budget view screenshot"
              loading="lazy"
              className="d-block mx-auto mx-md-0"
              style={{
                width: 'auto',
                maxWidth: '100%',
                height: 'auto',
                maxHeight: 500,
                objectFit: 'contain',
                borderRadius: 8,
                display: 'block',
              }}
            />
            <img
              src="/blog/budget1.jpg"
              alt="Budget template screenshot"
              loading="lazy"
              className="d-block mx-auto mx-md-0"
              style={{
                width: 'auto',
                maxWidth: '100%',
                height: 'auto',
                maxHeight: 500,
                objectFit: 'contain',
                borderRadius: 8,
                display: 'block',
              }}
            />
          </div>
        )}
      </div>
      <hr className="my-4" />
      <p>
        <Link to="/blog">{t('blog.post.backToList')}</Link>
      </p>
    </Container>
  );
};

export default BlogPost;
